import { Moon01Icon, SunIcon } from '@untitledui-icons/react/line'
import { useTranslation } from 'react-i18next'
import { useUIStore, type Theme } from '@/stores/uiStore'
import { cn } from '@/lib/utils'

interface ThemeToggleProps {
  /**
   * Show as icon-only button
   */
  iconOnly?: boolean
  /**
   * Additional CSS classes
   */
  className?: string
}

export function ThemeToggle({ iconOnly = false, className }: ThemeToggleProps) {
  const { t } = useTranslation()
  const theme = useUIStore((state) => state.theme)
  const resolvedTheme = useUIStore((state) => state.resolvedTheme)
  const setTheme = useUIStore((state) => state.setTheme)

  const isDark = resolvedTheme === 'dark'

  const handleToggle = () => {
    // Cycle through: light -> dark -> system -> light
    const nextTheme: Record<Theme, Theme> = {
      light: 'dark',
      dark: 'system',
      system: 'light',
    }
    setTheme(nextTheme[theme])
  }

  const handleSimpleToggle = () => {
    setTheme(isDark ? 'light' : 'dark')
  }

  if (iconOnly) {
    return (
      <button
        type="button"
        onClick={handleSimpleToggle}
        className={cn(
          'flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors',
          'hover:bg-gray-100 hover:text-gray-700',
          'dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
          className
        )}
        aria-label={isDark ? t('navigation.switch_to_light') : t('navigation.switch_to_dark')}
      >
        {isDark ? (
          <SunIcon className="h-5 w-5" />
        ) : (
          <Moon01Icon className="h-5 w-5" />
        )}
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={handleToggle}
      className={cn(
        'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
        'text-gray-700 hover:bg-gray-100',
        'dark:text-gray-300 dark:hover:bg-gray-800',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
        className
      )}
    >
      {isDark ? (
        <SunIcon className="h-5 w-5" />
      ) : (
        <Moon01Icon className="h-5 w-5" />
      )}
      <span>
        {theme === 'system'
          ? t('navigation.theme_system')
          : isDark
            ? t('navigation.theme_dark')
            : t('navigation.theme_light')}
      </span>
    </button>
  )
}

export default ThemeToggle
